import { ROUTES } from 'constant/routes';

const PREV_PATH_KEY = 'nav_prev_pathname';
const CURRENT_PATH_KEY = 'nav_current_pathname';

const readStorage = (key: string): string | null => {
  try {
    return sessionStorage.getItem(key);
  } catch {
    return null;
  }
};

/**
 * The same-origin referrer pathname for the current full page load, or null.
 * Recovers the prior page when tab history hasn't been tracked yet (first
 * load after deploy, cleared storage) — SPA navigations don't update the
 * referrer, so tracked history always takes precedence.
 */
const getReferrerPathname = (currentPathname: string): string | null => {
  try {
    if (!document.referrer) {
      return null;
    }

    const referrer = new URL(document.referrer);

    if (referrer.origin !== window.location.origin) {
      return null;
    }

    return referrer.pathname && referrer.pathname !== currentPathname
      ? referrer.pathname
      : null;
  } catch {
    return null;
  }
};

/**
 * Records SPA route changes so screens can tell where the user came from
 * (document.referrer only covers full page loads). Call once per pathname
 * change from a globally-mounted client component.
 */
export const trackPathname = (pathname: string): void => {
  try {
    const current = sessionStorage.getItem(CURRENT_PATH_KEY);

    if (current === pathname) {
      return;
    }

    // First run in this tab: recover the prior page from the full-load
    // referrer so a fresh tab/deploy doesn't look like a direct entry.
    const previous = current ?? getReferrerPathname(pathname) ?? '';

    sessionStorage.setItem(PREV_PATH_KEY, previous);
    sessionStorage.setItem(CURRENT_PATH_KEY, pathname);
  } catch {
    // Storage unavailable (SSR, private mode) — history simply stays unknown.
  }
};

/**
 * The in-app page visited before `currentPathname`, or null on a direct load.
 * Tolerates being read before `trackPathname` has processed the latest
 * navigation (effect ordering): if the stored "current" differs from the
 * caller's pathname, that stored value IS the previous page.
 */
export const getPreviousPathname = (currentPathname: string): string | null => {
  const current = readStorage(CURRENT_PATH_KEY);

  if (current && current !== currentPathname) {
    return current;
  }

  return (
    readStorage(PREV_PATH_KEY) || getReferrerPathname(currentPathname) || null
  );
};

/**
 * Prior pages the user should not be sent "back" to (per the design note on
 * the profile back arrow): completed-transaction pages and auth/onboarding
 * screens. Back navigation from these returns to the dashboard instead.
 */
const DASHBOARD_FALLBACK_PATHS: string[] = [
  ROUTES.cartSuccess,
  ROUTES.giftCardsShopSteps.finalStep,
  ROUTES.giftCardsShopSteps.createAccount,
  ROUTES.login,
  ROUTES.createAccount,
  ROUTES.createPassword,
  ROUTES.welcome
];

export const isNonReturnablePath = (path: string): boolean =>
  DASHBOARD_FALLBACK_PATHS.some(
    (prefix) => path === prefix || path.startsWith(`${prefix}/`)
  );
